import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { PROMO_POPUP_DEFAULT } from '../config/promoPopup';

/**
 * usePromoPopup — orchestrates when to show the homepage / SEO landing
 * promo popup and reports impressions + clicks so the coach can compare
 * A/B variants in the admin dashboard.
 *
 * Rules:
 *  - Fetch the active promo from `/api/promo-popup/active`. Falls back
 *    to the local default config if the API is unavailable.
 *  - Delay `delay_ms` before opening (gives visitors time to orient).
 *  - Skip entirely when `enabled === false` on the merged promo.
 *  - `cooldown_days` (default 1) is remembered in localStorage per
 *    `version` — bumping the version resets everyone.
 *  - A/B TEST: if `variants` is set on the promo, pick one randomly
 *    (50/50) per visitor with a sticky assignment, overlay its fields
 *    on top of the base promo, and report `variant` on every event.
 *  - Impression event fires when the popup actually opens; click event
 *    fires when the caller invokes `reportClick()`.
 */

const STORAGE_KEY = 'oc_promo_popup_dismissed_v1';
const VARIANT_KEY = 'oc_promo_variant_v1';   // sticky per browser + version
const API_BASE    = process.env.REACT_APP_BACKEND_URL || '';

const readDismissRecord = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw);
    return rec && typeof rec === 'object' ? rec : null;
  } catch (_e) { return null; }
};

const writeDismissRecord = (version) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version,
      dismissed_at: new Date().toISOString(),
    }));
  } catch (_e) { /* private browsing → ignore */ }
};

const isStillCoolingDown = (rec, promo) => {
  if (!rec) return false;
  if (!rec.version || rec.version !== promo.version) return false;   // new promo → show it
  const then = new Date(rec.dismissed_at).getTime();
  if (Number.isNaN(then)) return false;
  const ageDays = (Date.now() - then) / (1000 * 60 * 60 * 24);
  return ageDays < (Number(promo.cooldown_days) || 1);
};

// Sticky variant pick — a returning visitor keeps seeing the same variant
// so the same person can't inflate one arm's impressions.
const pickStickyVariant = (variants, version) => {
  if (!Array.isArray(variants) || variants.length === 0) return null;
  const stickyKey = `${VARIANT_KEY}:${version}`;
  try {
    const prev = localStorage.getItem(stickyKey);
    if (prev && variants.some(v => v.key === prev)) return prev;
  } catch (_e) { /* ignore */ }
  const chosen = variants[Math.floor(Math.random() * variants.length)];
  try { localStorage.setItem(stickyKey, chosen.key); } catch (_e) { /* ignore */ }
  return chosen.key;
};

const applyVariant = (base, variants, variantKey) => {
  if (!variantKey || !Array.isArray(variants)) return base;
  const v = variants.find(x => x.key === variantKey);
  if (!v) return base;
  const overrides = {};
  Object.entries(v).forEach(([k, val]) => {
    if (k === 'key' || k === 'enabled' || k === 'label') return;
    if (val === null || val === undefined || val === '') return;
    overrides[k] = val;
  });
  return { ...base, ...overrides };
};

const logEvent = (event, promo, extraPath) => {
  if (!promo?.version) return;
  const path = extraPath || (typeof window !== 'undefined' ? window.location.pathname : '/');
  axios.post(`${API_BASE}/api/promo-popup/event`, {
    version: promo.version,
    variant: promo.__variant || null,
    event,
    path,
  }, { timeout: 4000 }).catch(() => { /* fail silently */ });
};

/**
 * usePromoRotation — a heroban lévő ajánlatkártyához: NEM egy sticky változatot
 * ad, hanem az összes bekapcsolt promo típust, hogy folyamatosan váltogathassuk.
 * Ha egy típus sincs bekapcsolva, az alap tartalommal tér vissza.
 * `null` = a promo ki van kapcsolva vagy ütemezésen kívül van (ne jelenjen meg).
 */
export const usePromoRotation = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let promo = null;
      try {
        const { data } = await axios.get(`${API_BASE}/api/promo-popup/active`, { timeout: 5000 });
        promo = data?.promo || null;
      } catch (_e) { /* offline → nincs kártya */ }
      if (cancelled || !promo) return;

      const base = { ...PROMO_POPUP_DEFAULT, ...promo };
      if (!base.enabled) return;
      let variants = Array.isArray(promo.variants) && promo.variants.length > 0
        ? promo.variants
        : (PROMO_POPUP_DEFAULT.variants || []);
      variants = variants.filter((v) => v && v.enabled !== false);

      const list = variants.length > 0
        ? variants.map((v) => ({
          ...applyVariant(base, variants, v.key),
          __variant: v.key,
          __label: v.label || v.key,
        }))
        : [{ ...base, __variant: null, __label: 'Alap' }];

      setItems(list);
    })();
    return () => { cancelled = true; };
  }, []);

  return items;
};

/** Impression / click naplózás a hero kártyáról (a variáns kulcs mellé "·hero"). */
export const logPromoHeroEvent = (event, promo) => {
  if (!promo?.version) return;
  logEvent(event, { ...promo, __variant: `${promo.__variant || 'base'}·hero` });
};

export const usePromoPopup = ({ enabled = true } = {}) => {  const [promo, setPromo]   = useState(PROMO_POPUP_DEFAULT);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer;
    let cleanupScroll = null;

    (async () => {
      let base = { ...PROMO_POPUP_DEFAULT };
      let variants = PROMO_POPUP_DEFAULT.variants || null;
      try {
        const { data } = await axios.get(`${API_BASE}/api/promo-popup/active`, { timeout: 5000 });
        if (data && data.promo) {
          base = { ...PROMO_POPUP_DEFAULT, ...data.promo };
          if (Array.isArray(data.promo.variants) && data.promo.variants.length > 0) {
            variants = data.promo.variants;
          }
        }
      } catch (_e) { /* offline / 404 → keep defaults */ }

      // Csak a bekapcsolt típusok futnak. Ha mindet kikapcsolták, az alap
      // (szülő dokumentum) tartalma jelenik meg.
      variants = Array.isArray(variants)
        ? variants.filter((v) => v && v.enabled !== false)
        : null;

      if (cancelled) return;

      const variantKey = pickStickyVariant(variants, base.version || 'default');
      const merged = applyVariant(base, variants, variantKey);
      merged.__variant = variantKey; // internal — used for event logging

      setPromo(merged);

      if (!merged.enabled) return;
      const rec = readDismissRecord();
      if (isStillCoolingDown(rec, merged)) return;

      // Megjelenés: a beállított késleltetés UTÁN, vagy ha a látogató addig
      // legörget (bármelyik előbb). Naponta legfeljebb egyszer (cooldown).
      const open = () => {
        if (cancelled) return;
        setIsOpen(true);
        logEvent('impression', merged);
        window.removeEventListener('scroll', onScroll);
      };
      const onScroll = () => {
        if (window.scrollY > 600) {
          if (timer) clearTimeout(timer);
          open();
        }
      };
      const delay = Math.max(0, Number(merged.delay_ms) || 0);
      timer = setTimeout(open, delay);
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanupScroll = () => window.removeEventListener('scroll', onScroll);
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (cleanupScroll) cleanupScroll();
    };
  }, [enabled]);

  const close = useCallback(() => {
    setIsOpen(false);
    writeDismissRecord(promo.version || 'default');
  }, [promo]);

  const reportClick = useCallback(() => {
    logEvent('click', promo);
  }, [promo]);

  return { promo, isOpen, close, setIsOpen, reportClick };
};

export default usePromoPopup;
